import React from 'react'

const Dashboard = React.lazy(() => import('./views/dashboard/Dashboard'))
const Colors = React.lazy(() => import('./views/theme/colors/Colors'))
const Typography = React.lazy(() => import('./views/theme/typography/Typography'))

// Base
const Accordion = React.lazy(() => import('./views/base/accordion/Accordion'))
const Breadcrumbs = React.lazy(() => import('./views/base/breadcrumbs/Breadcrumbs'))
const Cards = React.lazy(() => import('./views/base/cards/Cards'))
const Carousels = React.lazy(() => import('./views/base/carousels/Carousels'))
const Collapses = React.lazy(() => import('./views/base/collapses/Collapses'))
const ListGroups = React.lazy(() => import('./views/base/list-groups/ListGroups'))
const Navs = React.lazy(() => import('./views/base/navs/Navs'))
const Paginations = React.lazy(() => import('./views/base/paginations/Paginations'))
const Placeholders = React.lazy(() => import('./views/base/placeholders/Placeholders'))
const Popovers = React.lazy(() => import('./views/base/popovers/Popovers'))
const Progress = React.lazy(() => import('./views/base/progress/Progress'))
const Spinners = React.lazy(() => import('./views/base/spinners/Spinners'))
const Tables = React.lazy(() => import('./views/base/tables/Tables'))
const Tooltips = React.lazy(() => import('./views/base/tooltips/Tooltips'))

// Buttons
const Buttons = React.lazy(() => import('./views/buttons/buttons/Buttons'))
const ButtonGroups = React.lazy(() => import('./views/buttons/button-groups/ButtonGroups'))
const LoadingButtons = React.lazy(() => import('./views/buttons/loading-buttons/LoadingButtons'))
const Dropdowns = React.lazy(() => import('./views/buttons/dropdowns/Dropdowns'))

//Forms
const ChecksRadios = React.lazy(() => import('./views/forms/checks-radios/ChecksRadios'))
const DatePicker = React.lazy(() => import('./views/forms/date-picker/DatePicker'))
const DateRangePicker = React.lazy(() => import('./views/forms/date-range-picker/DateRangePicker'))
const FloatingLabels = React.lazy(() => import('./views/forms/floating-labels/FloatingLabels'))
const FormControl = React.lazy(() => import('./views/forms/form-control/FormControl'))
const InputGroup = React.lazy(() => import('./views/forms/input-group/InputGroup'))
const Layout = React.lazy(() => import('./views/forms/layout/Layout'))
const MultiSelect = React.lazy(() => import('./views/forms/multi-select/MultiSelect'))
const Range = React.lazy(() => import('./views/forms/range/Range'))
const Select = React.lazy(() => import('./views/forms/select/Select'))
const TimePicker = React.lazy(() => import('./views/forms/time-picker/TimePicker'))
const Validation = React.lazy(() => import('./views/forms/validation/Validation'))

// Icons
const CoreUIIcons = React.lazy(() => import('./views/icons/coreui-icons/CoreUIIcons'))
const Flags = React.lazy(() => import('./views/icons/flags/Flags'))
const Brands = React.lazy(() => import('./views/icons/brands/Brands'))

// Notifications
const Alerts = React.lazy(() => import('./views/notifications/alerts/Alerts'))
const Badges = React.lazy(() => import('./views/notifications/badges/Badges'))
const Modals = React.lazy(() => import('./views/notifications/modals/Modals'))
const Toasts = React.lazy(() => import('./views/notifications/toasts/Toasts'))

const SmartTable = React.lazy(() => import('./views/smart-table/SmartTable'))

// Plugins
const Calendar = React.lazy(() => import('./views/plugins/calendar/Calendar'))
const Charts = React.lazy(() => import('./views/plugins/charts/Charts'))
const GoogleMaps = React.lazy(() => import('./views/plugins/google-maps/GoogleMaps'))

const Widgets = React.lazy(() => import('./views/widgets/Widgets'))

const Invoice = React.lazy(() => import('./views/apps/invoicing/Invoice'))

// Cube
const DashboardCube = React.lazy(() => import('./viewsCus/dashboard/Dashboard'))
const CenterList = React.lazy(() => import('./viewsCus/center/CenterList'))
const CenterRecordList = React.lazy(() => import('./viewsCus/centerRecord/CenterRecordList'))
const CenterRuleList = React.lazy(() => import('./viewsCus/centerRule/CenterRuleList'))
const DataMarketList = React.lazy(() => import('./viewsCus/dataMarket/DataMarketList'))
const NoticeList = React.lazy(() => import('./viewsCus/notice/NoticeList'))
const CenterDoorList = React.lazy(() => import('./viewsCus/centerDoor/CenterDoorList'))
const CenterDoorCodeList = React.lazy(() => import('./viewsCus/centerDoorCode/CenterDoorCodeList'))
const CenterDoorScheduleList = React.lazy(() =>
  import('./viewsCus/centerDoorSchedule/CenterDoorScheduleList'),
)
const CenterDoorScheduleRequestList = React.lazy(() =>
  import('./viewsCus/centerDoorScheduleRequest/CenterDoorScheduleRequestList'),
)
const CenterAccessLogList = React.lazy(() =>
  import('./viewsCus/centerAccessLog/CenterAccessLogList'),
)
const CenterMachineDetailsList = React.lazy(() =>
  import('./viewsCus/centerMachineDetails/CenterMachineDetailsList'),
)
const AdminLogList = React.lazy(() => import('./viewsCus/adminLog/AdminLogList'))

const AdminList = React.lazy(() => import('./viewsCus/admin/AdminList'))
const UserList = React.lazy(() => import('./viewsCus/user/UserList'))
const UserAuthMemoList = React.lazy(() => import('./viewsCus/userAuthMemo/UserAuthMemoList'))
const UserBlacklistList = React.lazy(() => import('./viewsCus/userBlacklist/UserBlacklistList'))

const GoodsCateList = React.lazy(() => import('./viewsCus/goodsCate/GoodsCateList'))
const GoodsCateUpload = React.lazy(() => import('./viewsCus/goodsCate/GoodsCateUpload'))
const GoodsTypeList = React.lazy(() => import('./viewsCus/goodsType/GoodsTypeList'))
const GoodsTypeUpload = React.lazy(() => import('./viewsCus/goodsType/GoodsTypeUpload'))
const GoodsList = React.lazy(() => import('./viewsCus/goods/GoodsList'))
const GoodsUpload = React.lazy(() => import('./viewsCus/goods/GoodsUpload'))
const GoodsListPredictOrderCnt = React.lazy(() =>
  import('./viewsCus/goods/GoodsListPredictOrderCnt'),
)

const GoodsInvenHistGoodsList = React.lazy(() =>
  import('./viewsCus/goodsInvenHist/GoodsInvenHistGoodsList'),
)
const GoodsInvenHistList = React.lazy(() => import('./viewsCus/goodsInvenHist/GoodsInvenHistList'))
const GoodsInvenHistUpload = React.lazy(() =>
  import('./viewsCus/goodsInvenHist/GoodsInvenHistUpload'),
)

const OrderList = React.lazy(() => import('./viewsCus/order/OrderList'))
const OrderCommentList = React.lazy(() => import('./viewsCus/orderComment/OrderCommentList'))
const OrderGoodsPwanDetailsList = React.lazy(() =>
  import('./viewsCus/orderGoodsPwanDetails/OrderGoodsPwanDetailsList'),
)
const OrderGoodsPwanDetailsSellList = React.lazy(() =>
  import('./viewsCus/orderGoodsPwanDetails/OrderGoodsPwanDetailsSellList'),
)
const OrderGoodsPwanDetailsBackList = React.lazy(() =>
  import('./viewsCus/orderGoodsPwanDetailsBack/OrderGoodsPwanDetailsBackList'),
)
const SendList = React.lazy(() => import('./viewsCus/send/SendList'))
const SendReceiveStat = React.lazy(() => import('./viewsCus/stat/SendReceiveStat'))

const OrderStat = React.lazy(() => import('./viewsCus/stat/OrderStat'))

const SpaceUseList = React.lazy(() => import('./viewsCus/spaceUse/SpaceUseList'))

// Cube User
const MenuUser_01 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_01'))
const MenuUser_02 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_02'))
const MenuUser_03 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_03'))
const MenuUser_04 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_04'))
const MenuUser_05 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_05'))
const MenuUser_06 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_06'))
const MenuUser_07 = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuUser_07'))
const MenuDashboardUser = React.lazy(() =>
  import('./viewsCus/_company/commonMenu/MenuDashboardUser'),
)
const MenuGuideUser = React.lazy(() => import('./viewsCus/_company/commonMenu/MenuGuideUser'))
const MenuNoticeListUser = React.lazy(() =>
  import('./viewsCus/_company/commonMenu/MenuNoticeListUser'),
)
const MenuCenterListUser = React.lazy(() =>
  import('./viewsCus/_company/commonMenu/MenuCenterListUser'),
)
const MenuCenterDetailUser = React.lazy(() =>
  import('./viewsCus/_company/commonMenu/MenuCenterDetailUser'),
)

const routes = [
  { path: '/', exact: true, name: 'Home' },

  // Cube
  { path: '/dashboardCube', name: '대쉬보드', element: DashboardCube },

  { path: '/center/centerList', name: '센터 관리', element: CenterList },
  { path: '/center/centerRecordList', name: '센터 관제일지', element: CenterRecordList },
  { path: '/center/noticeList', name: '공지사항', element: NoticeList },
  { path: '/center/centerRuleList', name: '센터약관 관리', element: CenterRuleList },

  { path: '/centerDoor/centerDoorList', name: '센터도어 관리', element: CenterDoorList },
  { path: '/centerDoor/centerDoorCodeList', name: '센터도어키', element: CenterDoorCodeList },
  {
    path: '/centerDoor/centerDoorScheduleList',
    name: '센터도어 스케쥴',
    element: CenterDoorScheduleList,
  },
  {
    path: '/centerDoor/centerDoorScheduleRequestList',
    name: '센터도어 스케쥴 요청',
    element: CenterDoorScheduleRequestList,
  },

  {
    path: '/centerMachine/centerMachineDetailsList',
    name: '센터장비상세 관리',
    element: CenterMachineDetailsList,
  },

  { path: '/user/adminList', name: '관리자 관리', element: AdminList },
  { path: '/user/userList', name: '사용자 관리', element: UserList },
  { path: '/user/userAuthMemoList', name: '사용자 메모', element: UserAuthMemoList },
  { path: '/user/userBlacklistList', name: '블랙리스트 관리', element: UserBlacklistList },

  { path: '/goods/goodsCateList', name: '카테고리 관리', element: GoodsCateList },
  { path: '/goods/goodsCateUpload', name: '카테고리 엑셀 등록', element: GoodsCateUpload },

  { path: '/goods/goodsTypeList', name: '상품유형 관리', element: GoodsTypeList },
  { path: '/goods/goodsTypeUpload', name: '상품유형 엑셀 등록', element: GoodsTypeUpload },

  { path: '/goods/goodsList', name: '상품 관리', element: GoodsList },
  { path: '/goods/goodsUpload', name: '상품 엑셀 등록', element: GoodsUpload },
  {
    path: '/goods/goodsListPredictOrderCnt',
    name: '상품별 예상주문건수',
    element: GoodsListPredictOrderCnt,
  },

  {
    path: '/goodsInvenHist/goodsInvenHistGoodsList',
    name: '재고 관리',
    element: GoodsInvenHistGoodsList,
  },
  {
    path: '/goodsInvenHist/goodsInvenHistUpload',
    name: '재고 엑셀 등록',
    element: GoodsInvenHistUpload,
  },
  {
    path: '/goodsInvenHist/goodsInvenHistList',
    name: '상품 입출고 이력',
    element: GoodsInvenHistList,
  },

  {
    path: '/service/centerAccessLogList',
    name: '센터도어 로그',
    element: CenterAccessLogList,
  },
  { path: '/service/orderList', name: '매출내역', element: OrderList },
  { path: '/service/spaceUseList', name: '예약내역', element: SpaceUseList },
  { path: '/service/orderCommentList', name: '주문메모', element: OrderCommentList },
  {
    path: '/pwan/orderGoodsPwanDetailsList',
    name: '대출내역',
    element: OrderGoodsPwanDetailsList,
  },
  {
    path: '/pwan/orderGoodsPwanDetailsSellList',
    name: '매입내역',
    element: OrderGoodsPwanDetailsSellList,
  },
  {
    path: '/pwan/orderGoodsPwanDetailsBackList',
    name: '이자상환내역',
    element: OrderGoodsPwanDetailsBackList,
  },

  { path: '/send/sendList', name: '문자발송', element: SendList },
  { path: '/send/sendReceiveStat', name: '문자발송통계', element: SendReceiveStat },

  {
    path: '/log/adminLogList',
    name: '관리자변경 로그',
    element: AdminLogList,
  },

  { path: '/order/orderStat', name: '매출통계', element: OrderStat },

  { path: '/data/dataMarketList', name: '시세관리', element: DataMarketList },

  // Cube User
  { path: '/commonMenu/MenuUser_01', name: 'MenuUser_01', element: MenuUser_01 },
  { path: '/commonMenu/MenuUser_02', name: 'MenuUser_02', element: MenuUser_02 },
  { path: '/commonMenu/MenuUser_03', name: 'MenuUser_03', element: MenuUser_03 },
  { path: '/commonMenu/MenuUser_04', name: 'MenuUser_04', element: MenuUser_04 },
  { path: '/commonMenu/MenuUser_05', name: 'MenuUser_05', element: MenuUser_05 },
  { path: '/commonMenu/MenuUser_06', name: 'MenuUser_06', element: MenuUser_06 },
  { path: '/commonMenu/MenuUser_07', name: 'MenuUser_06', element: MenuUser_07 },
  { path: '/commonMenu/MenuDashboardUser', name: 'MenuDashboardUser', element: MenuDashboardUser },
  { path: '/commonMenu/MenuGuideUser', name: 'MenuGuideUser', element: MenuGuideUser },
  {
    path: '/commonMenu/MenuNoticeListUser',
    name: 'MenuNoticeListUser',
    element: MenuNoticeListUser,
  },
  {
    path: '/commonMenu/MenuCenterListUser',
    name: 'MenuCenterListUser',
    element: MenuCenterListUser,
  },
  {
    path: '/commonMenu/MenuCenterDetailUser',
    name: 'MenuCenterDetailUser',
    element: MenuCenterDetailUser,
  },

  // default
  { path: '/dashboard', name: 'Dashboard', element: Dashboard },
  { path: '/theme', name: 'Theme', element: Colors, exact: true },
  { path: '/theme/colors', name: 'Colors', element: Colors },
  { path: '/theme/typography', name: 'Typography', element: Typography },
  { path: '/base', name: 'Base', element: Cards, exact: true },
  { path: '/base/accordion', name: 'Accordion', element: Accordion },
  { path: '/base/breadcrumbs', name: 'Breadcrumbs', element: Breadcrumbs },
  { path: '/base/cards', name: 'Cards', element: Cards },
  { path: '/base/carousels', name: 'Carousel', element: Carousels },
  { path: '/base/collapses', name: 'Collapse', element: Collapses },
  { path: '/base/list-groups', name: 'List Groups', element: ListGroups },
  { path: '/base/navs', name: 'Navs', element: Navs },
  { path: '/base/paginations', name: 'Paginations', element: Paginations },
  { path: '/base/placeholders', name: 'Placeholders', element: Placeholders },
  { path: '/base/popovers', name: 'Popovers', element: Popovers },
  { path: '/base/progress', name: 'Progress', element: Progress },
  { path: '/base/spinners', name: 'Spinners', element: Spinners },
  { path: '/base/tables', name: 'Tables', element: Tables },
  { path: '/base/tooltips', name: 'Tooltips', element: Tooltips },
  { path: '/buttons', name: 'Buttons', element: Buttons, exact: true },
  { path: '/buttons/buttons', name: 'Buttons', element: Buttons },
  { path: '/buttons/button-groups', name: 'Button Groups', element: ButtonGroups },
  { path: '/buttons/loading-buttons', name: 'Loading Buttons', element: LoadingButtons },
  { path: '/buttons/dropdowns', name: 'Dropdowns', element: Dropdowns },
  { path: '/forms', name: 'Forms', element: FormControl, exact: true },
  { path: '/forms/form-control', name: 'Form Control', element: FormControl },
  { path: '/forms/select', name: 'Select', element: Select },
  { path: '/forms/multi-select', name: 'Multi Select', element: MultiSelect },
  { path: '/forms/checks-radios', name: 'Checks & Radios', element: ChecksRadios },
  { path: '/forms/range', name: 'Range', element: Range },
  { path: '/forms/input-group', name: 'Input Group', element: InputGroup },
  { path: '/forms/floating-labels', name: 'Floating Labels', element: FloatingLabels },
  { path: '/forms/date-picker', name: 'Date Picker', element: DatePicker },
  { path: '/forms/date-range-picker', name: 'Date Range Picker', element: DateRangePicker },
  { path: '/forms/time-picker', name: 'Time Picker', element: TimePicker },
  { path: '/forms/layout', name: 'Layout', element: Layout },
  { path: '/forms/validation', name: 'Validation', element: Validation },
  { path: '/icons', exact: true, name: 'Icons', element: CoreUIIcons },
  { path: '/icons/coreui-icons', name: 'CoreUI Icons', element: CoreUIIcons },
  { path: '/icons/flags', name: 'Flags', element: Flags },
  { path: '/icons/brands', name: 'Brands', element: Brands },
  { path: '/notifications', name: 'Notifications', element: Alerts, exact: true },
  { path: '/notifications/alerts', name: 'Alerts', element: Alerts },
  { path: '/notifications/badges', name: 'Badges', element: Badges },
  { path: '/notifications/modals', name: 'Modals', element: Modals },
  { path: '/notifications/toasts', name: 'Toasts', element: Toasts },
  { path: '/plugins', name: 'Plugins', element: Calendar, exact: true },
  { path: '/plugins/calendar', name: 'Calendar', element: Calendar },
  { path: '/plugins/charts', name: 'Charts', element: Charts },
  { path: '/plugins/google-maps', name: 'GoogleMaps', element: GoogleMaps },
  { path: '/smart-table', name: 'Smart Table', element: SmartTable },
  { path: '/widgets', name: 'Widgets', element: Widgets },
  { path: '/apps', name: 'Apps', element: Invoice, exact: true },
  { path: '/apps/invoicing', name: 'Invoice', element: Invoice, exact: true },
  { path: '/apps/invoicing/invoice', name: 'Invoice', element: Invoice },
  { path: '/apps/email', name: 'Email', exact: true },
  { path: '/apps/email/inbox', name: 'Inbox', exact: true },
  { path: '/apps/email/compose', name: 'Compose', exact: true },
  { path: '/apps/email/message', name: 'Message', exact: true },
]

export default routes
